import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import {coordinateSystems, preferenceGroups} from '@src/constants';
import {
  AREA_UNITS,
  DISTANCE_UNITS,
  MEASUREMENT_SYSTEMS,
  UNIT_SYMBOLS,
} from '@src/constants/measureUnits.ts';
import formatNumber from '@src/utils/formatNumber.js';
import PropTypes from 'prop-types';
import React, {Fragment} from 'react';
import {connect} from 'react-redux';
import Setting from './Setting.jsx';

const formatMeasurementExample = (value, unit) =>
  `${formatNumber(value)} ${UNIT_SYMBOLS[unit]}`;

const Header = props => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      marginBlockStart: 1,
      marginInline: 2,
    }}
    {...props}
  />
);

const Settings = ({measurementSystem}) => {
  return (
    <List sx={{overflow: 'auto', height: '100%'}}>
      <Header color="inherit">Coordinate System</Header>
      <Setting
        value={coordinateSystems.dd}
        group={preferenceGroups.coordinateSystem}
        description="Decimal degrees"
        example="37.77° N, 122.41° W"
      />
      <Setting
        value={coordinateSystems.dms}
        group={preferenceGroups.coordinateSystem}
        description="Degrees, Minutes, Seconds"
        example="37° 46′ 24″ N 122° 24′ 40″ W"
      />
      <Setting
        value={coordinateSystems.mgrs}
        group={preferenceGroups.coordinateSystem}
        description="Military Grid Reference System"
        example="10SGJ 068 446"
      />
      <Divider component="li" sx={{marginBlock: 1}} />
      <Header>Measurement System</Header>
      <Setting
        value={MEASUREMENT_SYSTEMS.metric}
        group={preferenceGroups.measurementSystem}
        description="Metric"
      />
      <Setting
        value={MEASUREMENT_SYSTEMS.usc}
        group={preferenceGroups.measurementSystem}
        description="United States Customary"
      />
      <Header>Distance</Header>
      {measurementSystem === MEASUREMENT_SYSTEMS.metric ? (
        <>
          <Setting
            value={DISTANCE_UNITS.metric.m}
            group={preferenceGroups.distanceUnitsMetric}
            description="Meters"
            example={formatMeasurementExample(1000, DISTANCE_UNITS.metric.m)}
          />
          <Setting
            value={DISTANCE_UNITS.metric.km}
            group={preferenceGroups.distanceUnitsMetric}
            description="Kilometers"
            example={formatMeasurementExample(1, DISTANCE_UNITS.metric.km)}
          />
        </>
      ) : (
        <>
          <Setting
            value={DISTANCE_UNITS.usc.ft}
            group={preferenceGroups.distanceUnitsUSC}
            description="Feet"
            example={formatMeasurementExample(5280, DISTANCE_UNITS.usc.ft)}
          />
          <Setting
            value={DISTANCE_UNITS.usc.mi}
            group={preferenceGroups.distanceUnitsUSC}
            description="Miles"
            example={formatMeasurementExample(1, DISTANCE_UNITS.usc.mi)}
          />
          <Setting
            value={DISTANCE_UNITS.usc.nmi}
            group={preferenceGroups.distanceUnitsUSC}
            description="Nautical Miles"
            example={formatMeasurementExample(0.87, DISTANCE_UNITS.usc.nmi)}
          />
        </>
      )}
      <Header>Area</Header>
      {measurementSystem === MEASUREMENT_SYSTEMS.metric ? (
        <Fragment>
          <Setting
            value={AREA_UNITS.metric.m2}
            group={preferenceGroups.areaUnitsMetric}
            description="Square Meters"
            example={formatMeasurementExample(1000000, AREA_UNITS.metric.m2)}
          />
          <Setting
            value={AREA_UNITS.metric.km2}
            group={preferenceGroups.areaUnitsMetric}
            description="Square Kilometers"
            example={formatMeasurementExample(1, AREA_UNITS.metric.km2)}
          />
          <Setting
            value={AREA_UNITS.metric.ha}
            group={preferenceGroups.areaUnitsMetric}
            description="Hectares"
            example={formatMeasurementExample(100, AREA_UNITS.metric.ha)}
          />
        </Fragment>
      ) : (
        <Fragment>
          <Setting
            value={AREA_UNITS.usc.ft2}
            group={preferenceGroups.areaUnitsUSC}
            description="Square Feet"
            example={formatMeasurementExample(27880000, AREA_UNITS.usc.ft2)}
          />
          <Setting
            value={AREA_UNITS.usc.mi2}
            group={preferenceGroups.areaUnitsUSC}
            description="Square Miles"
            example={formatMeasurementExample(1, AREA_UNITS.usc.mi2)}
          />
          <Setting
            value={AREA_UNITS.usc.nmi2}
            group={preferenceGroups.areaUnitsUSC}
            description="Square Nautical Miles"
            example={formatMeasurementExample(0.75, AREA_UNITS.usc.nmi2)}
          />
          <Setting
            value={AREA_UNITS.usc.ac}
            group={preferenceGroups.areaUnitsUSC}
            description="Acres"
            example={formatMeasurementExample(640, AREA_UNITS.usc.ac)}
          />
        </Fragment>
      )}
    </List>
  );
};

Settings.propTypes = {
  measurementSystem: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  measurementSystem: state.settings.measurementSystem,
});

export default connect(mapStateToProps)(Settings);
